import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'convert-csv-json-xml-yaml-markdown-html',
  title: 'Convert Between CSV, JSON, XML, YAML, Markdown and HTML',
  description:
    'Free online converter for text formats: CSV to JSON, JSON to CSV, Markdown to HTML, HTML to Markdown, XML to JSON, tables to CSV and more, in your browser.',
  category: 'Text editor',
  publishedDate: '2026-11-03',
  affiliateCategory: 'formats',
  body: `
The **Convert format** box in the [Text Editor](/text-editor) converts between eight formats: plain text, Markdown, HTML, CSV, TSV, JSON, XML and YAML. Paste your content and the editor **detects the format automatically** (it's shown as "detected" in the **From** list); pick a **To** format and click **Convert**.

## What converts to what

| From | To |
|---|---|
| Plain text | Markdown, HTML |
| Markdown | Plain text, HTML, CSV, TSV, JSON |
| HTML | Plain text, Markdown, CSV, TSV, JSON |
| CSV / TSV | JSON, Markdown table, HTML table, XML, YAML, TSV / CSV |
| JSON | CSV, TSV, Markdown table, HTML table, XML, YAML |
| XML | JSON, YAML |

YAML is output-only.

## Popular recipes

**Spreadsheet → JSON for code.** Copy cells from Excel or Google Sheets and paste them — they arrive as TSV. Convert **TSV → JSON** to get an array of objects keyed by the header row. Numbers and \`true\`/\`false\` become real JSON values; values like \`007\` keep their leading zeros as text.

**API response → spreadsheet.** Paste JSON and convert **JSON → CSV**. Nested objects are flattened into dotted columns (\`user.name\`, \`user.email\`), and the first array of records is used if the data is wrapped (e.g. \`{"data": [...]}\`).

**Table for docs or a README.** Convert CSV or JSON → **Markdown** to get a neatly aligned Markdown table.

**Pull a table out of a document.** Converting Markdown or HTML to CSV, TSV or JSON **extracts the first table** in the document — handy for tables inside converted Word files or copied web pages.

**Web page → clean notes.** Paste HTML and convert **HTML → Markdown** to keep headings, links and lists without the markup clutter.

**Config files.** Convert JSON or XML → **YAML** for tools like Docker Compose, Kubernetes or GitHub Actions. In XML → JSON, attributes become \`"@name"\` keys and element text becomes \`"#text"\` when an element also has attributes.

## CSV to Excel (.xlsx)

Have a CSV and need a real spreadsheet? Open it in the editor (or paste it), then choose **Download → Excel workbook**.

- **Delimiter:** auto-detected — comma, semicolon, tab or pipe — or choose *Other…* for any character. A live preview shows the columns so you can confirm they split correctly.
- **European CSVs:** semicolon files usually write decimals with a comma (\`1.234,56\`); the decimal setting switches automatically and you can change it.
- **Real numbers, dates and TRUE/FALSE:** plain numbers, percentages (\`12.5%\`), currency (\`$1,299.00\`) and ISO dates (\`2026-11-03\`) become proper Excel values you can sum, sort and chart.
- **Nothing gets mangled:** ZIP/postal codes and IDs with leading zeros (\`01067\`), numbers longer than 15 digits (card or account numbers) and phone numbers written with + or dashes stay as text. If any value in a column has a leading zero, the whole column stays text.
- **Ready to use:** the first row becomes a header with filter buttons, and columns are sized to fit.

Files exported from Excel on Windows are often saved in an older encoding; the editor detects this when you open them, so accents like *é* and *ü* come through correctly.

## Tips

- **Override detection** if the guess is wrong: choose the right format in **From**. The ⇄ button swaps From and To when the reverse conversion is possible.
- **Convert just part of the text:** select it first — only the selection is converted, the rest stays untouched.
- **Check before you convert:** switch to **Split** or **Preview** view to see Markdown and HTML rendered, CSV/TSV as a table, or JSON pretty-printed.
- **Format and validate JSON:** under *Encode, decode & format*, use **Format JSON**, **Minify JSON** or **Sort JSON keys**. If the JSON is invalid you'll get an error message pointing to the problem.
- **Save in the right format:** the **Download** menu saves the text as \`.txt\`, \`.md\`, \`.html\`, \`.csv\`, \`.tsv\`, \`.json\`, \`.xml\` or \`.yaml\` — or as a formatted PDF.

Every conversion is a single undo step, so experiment freely: **Ctrl+Z** brings back the original.
`,
};

export default guide;
